import React, { useEffect, useRef, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { getModifiers, getStyleModifiers } from '../../../helpers/series';
import Skeleton from '../../../../shared/Skeleton';
import IntlDayPicker, { Day } from '@showclix-shared/IntlDayPicker';

const noop = () => {
  // do nothing
};

const DayPickerContext = createContext({});
const useDayPickerContext = () => {
  const ctx = useContext(DayPickerContext);
  return ctx;
}

const CustomNextMonthButton = ({ disabled: disabledProp, ...rest }) => {
  const disabled = typeof disabledProp === 'undefined' ? rest['aria-disabled'] : disabledProp;
  const { setNextButtonRef, prevButtonRef } = useDayPickerContext();
  const prevDisabled = useRef(disabled);

  const onDisable = () => {
    prevButtonRef.current?.focus();
  }
  
  useEffect(() => {
    if (!prevDisabled.current && disabled) {
      onDisable();
    }
    prevDisabled.current = disabled;
  }, [disabled])

  return <button ref={(ref) => setNextButtonRef(ref)} {...rest} disabled={rest['aria-disabled']} />
}

const CustomPreviousMonthButton = ({ disabled: disabledProp, ...rest }) => {
  const disabled = typeof disabledProp === 'undefined' ? rest['aria-disabled'] : disabledProp;
  const { setPrevButtonRef, nextButtonRef } = useDayPickerContext();
  const prevDisabled = useRef(disabled);

  const onDisable = () => {
    nextButtonRef.current?.focus();
  }
  
  useEffect(() => {
    if (!prevDisabled.current && disabled) {
      onDisable();
    }
    prevDisabled.current = disabled;
  }, [disabled])

  return <button ref={(ref) => setPrevButtonRef(ref)} {...rest} disabled={rest['aria-disabled']} />
}

const CustomDay = (props) => {
  return <Day {...props} aria-current={props['data-today'] ? 'date' : undefined} />
}

/**
 * Shared logic for applying modifiers and events to IntlDayPicker
 */
const DayPicker = (props) => {
  const prevButtonRef = useRef();
  const nextButtonRef = useRef();

  const setPrevButtonRef = (ref) => {
    prevButtonRef.current = ref;
  }

  const setNextButtonRef = (ref) => {
    nextButtonRef.current = ref;
  }

  if (props.asSkeleton) {
    return (
      <IntlDayPicker
        rootClassName="checkout-rdp-root"
        locale={props.locale}
        pagedNavigation={false}
        numberOfMonths={props.numberOfMonths}
        disableNavigation={false}
        components={{
          DayButton: () => <Skeleton height="32px" width="32px" borderRadius="50%" />,
          CaptionLabel: () => <Skeleton height="25px" width="50%" borderRadius="12px" />,
          Weekday: () => null,
        }}
      />
    );
  }

  const modifiers = getModifiers(
    props.eventDates,
    props.calendarColors,
    props.calendarDescriptions,
    props.inventoryFilterResults,
  );
  const calendarStyle = getStyleModifiers(props.accentColor, modifiers);
  const allModifiers = {
    ...modifiers,
    soldOutDays: props.soldOutDays,
  };

  if (props.selectedDay) {
    allModifiers.selectedDay = new Date(props.selectedDay);
  }

  let providedProps = { ...props };
  if (props.loading) {
    providedProps.onDayClick = noop;
  }

  if (props.currentMonth) {
    providedProps.month = props.currentMonth;
  }

  return (
    <DayPickerContext.Provider value={{ setPrevButtonRef, setNextButtonRef, prevButtonRef, nextButtonRef }}>
      <IntlDayPicker
        rootClassName="checkout-rdp-root"
        locale={props.locale}
        modifiers={allModifiers}
        modifiersStyles={calendarStyle}
        modifiersClassNames={{
          eventDates: 'rdp-event-date',
          soldOutDays: 'rdp-sold-out',
        }}
        pagedNavigation={false}
        numberOfMonths={props.numberOfMonths}
        disableNavigation={false}
        className={props.fullLoading ? 'loading loading--full' : props.loading ? 'loading' : 'loaded'}
        components={{
          NextMonthButton: CustomNextMonthButton,
          PreviousMonthButton: CustomPreviousMonthButton,
          Day: CustomDay,
        }}
        {...providedProps}
      />
    </DayPickerContext.Provider>
  );
};

DayPicker.propTypes = {
  locale: PropTypes.string.isRequired,
  selectedDay: PropTypes.string,
  startingMonth: PropTypes.instanceOf(Date),
  endingMonth: PropTypes.instanceOf(Date),
  selectedDays: PropTypes.array,
  disabledDays: PropTypes.array,
  onDayClick: PropTypes.func,
  onMonthChange: PropTypes.func,
  numberOfMonths: PropTypes.number,
  fromMonth: PropTypes.instanceOf(Date),
  initialMonth: PropTypes.instanceOf(Date),
  toMonth: PropTypes.instanceOf(Date),
  currentMonth: PropTypes.instanceOf(Date),
  soldOutDays: PropTypes.array,
  eventDates: PropTypes.array,
  calendarColors: PropTypes.object,
  calendarDescriptions: PropTypes.object,
  accentColor: PropTypes.string,
  loading: PropTypes.bool,
  fullLoading: PropTypes.bool,
  inventoryFilterResults: PropTypes.object,
  asSkeleton: PropTypes.bool,
};

export default DayPicker;
